import React, { useState } from "react";
import { CpuChipIcon, ServerIcon, CloudIcon, CommandLineIcon, CodeBracketIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import type { TechStackSelectorProps } from "../technicalSolutionPhase.types";
import type { TechChoice } from "@/features/Projects";

const stackCategories = [
  {
    key: "frontend" as const,
    name: "Frontend",
    icon: CodeBracketIcon,
    color: "text-blue-600 bg-blue-50",
  },
  {
    key: "backend" as const,
    name: "Backend",
    icon: ServerIcon,
    color: "text-green-600 bg-green-50",
  },
  {
    key: "database" as const,
    name: "Banco de Dados",
    icon: CpuChipIcon,
    color: "text-purple-600 bg-purple-50",
  },
  {
    key: "infrastructure" as const,
    name: "Infraestrutura",
    icon: CloudIcon,
    color: "text-orange-600 bg-orange-50",
  },
  {
    key: "devops" as const,
    name: "DevOps",
    icon: CommandLineIcon,
    color: "text-red-600 bg-red-50",
  },
];

export default function TechStackSelector({
  technicalSolution,
  onChange,
  isGenerating,
  defaultExpanded = false,
}: TechStackSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const handleTechChoiceChange = (
    category: keyof typeof technicalSolution.recommendedStack,
    index: number,
    field: keyof TechChoice,
    value: string | string[]
  ) => {
    const newStack = { ...technicalSolution.recommendedStack };
    const choices = [...(newStack[category] || [])];
    
    if (choices[index]) {
      choices[index] = { ...choices[index], [field]: value };
      newStack[category] = choices;
      
      onChange({
        ...technicalSolution,
        recommendedStack: newStack,
      });
    }
  };

  const handleAddTech = (category: keyof typeof technicalSolution.recommendedStack) => {
    const newStack = { ...technicalSolution.recommendedStack };
    newStack[category] = [
      ...(newStack[category] || []),
      {
        name: "",
        justification: "",
        alternatives: [],
      },
    ];
    
    onChange({
      ...technicalSolution,
      recommendedStack: newStack,
    });
  };

  const handleRemoveTech = (
    category: keyof typeof technicalSolution.recommendedStack,
    index: number
  ) => {
    const newStack = { ...technicalSolution.recommendedStack };
    newStack[category] = (newStack[category] || []).filter((_, i) => i !== index);
    
    onChange({
      ...technicalSolution,
      recommendedStack: newStack,
    });
  };

  if (isGenerating) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <CpuChipIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Stack Tecnológica</h3>
        </div>
        <div className="text-center py-8">
          <div className="inline-flex items-center space-x-2 text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>Analisando tecnologias...</span>
          </div>
        </div>
      </div>
    );
  }

  // Check if there's any tech content
  const hasTechContent = stackCategories.some(({ key }) => 
    (technicalSolution?.recommendedStack?.[key] || []).length > 0
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <CpuChipIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Stack Tecnológica</h3>
          {hasTechContent && !isExpanded && (
            <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              Configurada
            </span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800"
        >
          <span>{isExpanded ? "Minimizar" : "Expandir"}</span>
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-6">
          {stackCategories.map(({ key, name, icon: Icon, color }) => (
          <div key={key} className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={clsx("p-1.5 rounded", color)}>
                  <Icon className="h-4 w-4" />
                </div>
                <h4 className="font-medium text-gray-900">{name}</h4>
              </div>
              <button
                onClick={() => handleAddTech(key)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                + Adicionar
              </button>
            </div>

            <div className="space-y-3">
              {(technicalSolution.recommendedStack[key] || []).map((tech, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tecnologia
                      </label>
                      <input
                        type="text"
                        value={tech.name}
                        onChange={(e) =>
                          handleTechChoiceChange(key, index, "name", e.target.value)
                        }
                        className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Nome da tecnologia"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Versão
                      </label>
                      <input
                        type="text"
                        value={tech.version || ""}
                        onChange={(e) =>
                          handleTechChoiceChange(key, index, "version", e.target.value)
                        }
                        className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="ex: 18.x"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Justificativa
                    </label>
                    <textarea
                      value={tech.justification}
                      onChange={(e) =>
                        handleTechChoiceChange(key, index, "justification", e.target.value)
                      }
                      rows={2}
                      className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Por que escolher esta tecnologia?"
                    />
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <div className="flex flex-wrap gap-1">
                      {tech.alternatives.map((alt, altIndex) => (
                        <span
                          key={altIndex}
                          className="inline-flex items-center px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded"
                        >
                          {alt}
                        </span>
                      ))}
                    </div>
                    <button
                      onClick={() => handleRemoveTech(key, index)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remover
                    </button>
                  </div>
                </div>
              ))}

              {(technicalSolution.recommendedStack[key] || []).length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Nenhuma tecnologia adicionada ainda
                </div>
              )}
            </div>
          </div>
          ))}
        </div>
      )}

      {!isExpanded && hasTechContent && (
        <div className="text-center py-4">
          <p className="text-sm text-gray-600">
            Stack tecnológica configurada com{" "}
            {stackCategories.reduce((total, { key }) => total + (technicalSolution.recommendedStack[key] || []).length, 0)}{" "}
            tecnologias
          </p>
        </div>
      )}

      {!isExpanded && !hasTechContent && (
        <div className="text-center py-8 text-gray-500">
          <CpuChipIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Clique em "Expandir" para configurar a stack tecnológica</p>
        </div>
      )}
    </div>
  );
}